<script lang="ts" setup>
import { useSdk } from '~/sdk';
import { getCmsImage, assetPath } from '@/composables/useCms';
import { handleBlogCmsError, useBlog } from '@/composables/useBlog';
import { useBlogPaths } from '@/composables/useBlogPaths';

const { locale, t } = useI18n();
const route = useRoute();
const { getIndexPath } = useBlogPaths();
const routeSlug = computed(() => {
  const slug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug;

  return String(slug ?? '');
});

const { data, error, status } = await useAsyncData(computed(() => `author-${locale.value}-${routeSlug.value}`), async () => {
  return useSdk().cms.getContent({
    entry: {
      section: 'authors',
      slug: routeSlug.value,
    },
  });
});

handleBlogCmsError(error);

if (!data.value) {
  throw createError({
    statusCode: 404,
    message: t('Page not found'),
  });
}

const author = shallowRef(data.value);

watch([status, data], ([requestStatus, value]) => {
  if (requestStatus !== 'success') return;

  if (!value) {
    showError(createError({
      statusCode: 404,
      message: t('Page not found'),
      fatal: true,
    }));
    return;
  }

  // Keep the last successful author while a reactive route refetch is pending.
  // This prevents useBlog from briefly issuing an unfiltered news request.
  author.value = value;
});

useSeoMeta({
  title: () => author.value?.title,
  ogTitle: () => author.value?.title,
});

const authorId = computed(() => author.value?.id ?? null);
const {
  currentPage,
  data: authorArticles,
  hasPagination,
  loading,
  pageSize,
  totalItems,
} = await useBlog({ authorId });
</script>

<template>
  <div v-if="author" class="py-16">
    <VContainer>
      <Breadcrumbs :breadcrumbs="[
        { title: t('Home'), url: `/${locale}` },
        { title: 'Blog', url: getIndexPath() },
        { title: author.title, url: '' },
      ]" class="mb-10" />

      <div class="flex flex-col md:flex-row gap-8 mb-16">
        <div class="shrink-0">
          <NuxtImg
            v-if="author.image?.[0]?.url"
            :src="getCmsImage(author.image)"
            :alt="author.title"
            class="w-48 h-48 object-cover rounded"
          />
        </div>
        <div>
          <h1 class="text-3xl font-bold mb-1">{{ author.title }}</h1>
          <p v-if="author.role" class="text-gray-500 mb-3">{{ author.role }}</p>
          <div v-if="author.socials?.length" class="flex gap-3 mb-4">
            <a
              v-for="social in author.socials"
              :key="social.alias"
              :href="social.theurl"
              target="_blank"
              rel="noopener noreferrer"
              class="hover:opacity-70 transition-opacity"
            >
              <NuxtImg v-if="social.icon?.[0]?.url" :src="assetPath(social.icon[0].url)" :alt="social.alias" class="w-6 h-6" />
              <span v-else>{{ social.alias }}</span>
            </a>
          </div>
          <div v-if="author.body" class="prose max-w-none" v-html="author.body"></div>
        </div>
      </div>

      <h2 class="text-2xl font-bold mb-6">{{ t('blog.Author articles') }}</h2>
      <div v-if="authorArticles.length > 0" class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        <BlogCard v-for="article in authorArticles" :key="article.id" :article="article" />
      </div>
      <p v-else-if="!loading" class="text-gray-500">{{ t('blog.No articles found') }}</p>

      <PaginationQuery
        v-if="hasPagination"
        :current-page="currentPage"
        :total-items="totalItems"
        :page-size="pageSize"
      />
    </VContainer>
  </div>
</template>
